import * as React from "react";
import { Button, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import coursePartService from "src/coursepart/CoursePartService";
import { BatchPublishCheckResponse } from "src/coursepart/domain/BatchPublishCheckResponse";
import { LanguageService } from "src/utils/languageService";
import { AffectedClassesList } from "./AffectedClassesList";
import { emptyStudentClassGroups, hasStudentClasses } from "src/components/StudentClassGroups/studentClassGroups";
import { PublishInBatchResponse } from "src/coursepart/domain/PublishInBatchResponse";
import { AffectedPartsList } from "./AffectedPartsList";
import { PublishedPartsList } from "./PublishedPartsList";
import WaitRipple from "src/components/WaitRipple";
import { PROD } from "src/utils/constants";


export interface PublishAllPartsDialogProps {
    languageService: LanguageService;
    onCancel: () => void;
    partIds: string[]
}


export const PublishAllPartsDialog: React.FC<PublishAllPartsDialogProps> = ({
    onCancel,
    languageService,
    partIds
}) => {

    const t = languageService;
    const [requestData, setRequestData] = React.useState<BatchPublishCheckResponse>({ Parts: [], StudentClasses: emptyStudentClassGroups() });
    const [publishedData, setPublishedData] = React.useState<PublishInBatchResponse[]>([]);
    const [working, setWorking] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | undefined>();
    const isMounted = React.useRef(true);

    /**
     * isMounted to track if we have not been unMounted during process
     */
    React.useEffect(() => {
        if( PROD )
            return () => { isMounted.current = false; };
    }, [])

    React.useEffect(() => {
        setWorking(true);
        coursePartService.batchPublishPartsRequest(partIds)
            .then(data => {
                if (isMounted.current) {
                    setRequestData(data || { Parts: [], StudentClasses: emptyStudentClassGroups() });
                }
            }).catch(error => {
                if (isMounted.current) {
                    console.error("Failed to request publish parts:", error);
                    setError(t.getText("servererror"));
                }
            }).finally(() => {
                if (isMounted.current) {
                    setWorking(false);
                }
            });

    }, [partIds, t])

    const publish = React.useCallback(() => {
        if (requestData.Parts.length === 0) return;

        setWorking(true);
        const partIds = requestData.Parts.map(d => d.PartId);

        coursePartService.batchPublishParts(partIds)
            .then(data => {
                if (isMounted.current) {
                    setPublishedData(data || []);
                    setRequestData({ Parts: [], StudentClasses: emptyStudentClassGroups() });
                }
            })
            .catch(error => {
                console.error("Failed to publish parts:", error);
                if (isMounted.current) {
                    setError(t.getText("servererror"));
                }

            }).finally(() => {
                if (isMounted.current) {
                    setWorking(false);
                }
            });

    }, [requestData, t]);

    return <>
        <span className="moveable" data-dialog-draggable>
            <ModalHeader>{t.getText("publish.all.parts")}</ModalHeader>
        </span>
        <ModalBody>

            {working && <WaitRipple />}

            {error && <div className="my-5 alert alert-danger preserve-white">{error}</div>}

            {hasStudentClasses(requestData.StudentClasses) && <div>
                <h3>{t.getText("classes")}</h3>
                <div className="mb-5">{t.getText("publish.warning")}</div>
                <AffectedClassesList classes={requestData.StudentClasses} />
            </div>
            }

            {requestData.Parts.length > 0 &&
                <div className="mt-5">
                    <h3 className="my-3">{t.getText("courseparts")}</h3>
                    <AffectedPartsList parts={requestData.Parts} />
                    <Button type="button" className="my-3 btn btn-primary" onClick={publish}>{t.getText("publish")}</Button>
                </div>
            }

            {publishedData.length > 0 &&
                <div className="mt-5">
                    <h3 className="my-3">{t.getText("published")}</h3>
                    <PublishedPartsList parts={publishedData} />
                </div>
            }


        </ModalBody>
        <ModalFooter>
            <Button type="button" className="btn btn-warning" onClick={onCancel}>{t.getText("cancel")}</Button>
        </ModalFooter>
    </>
};
